import React from "react";
import type { RootView } from "../shared/productTypes";
import { FinanceConfigProvider } from "./context/FinanceConfigContext";
import { BusinessHoursProvider } from "./context/BusinessHoursContext";
import { WOEmailsProvider, useWOEmails, EmailThread } from "./context/WOEmailsContext";
import { Sidebar, ActivePage } from "./components/Sidebar/Sidebar";
import { AssetsListPage } from "./pages/AssetsListPage";
import { AssetDetailPage } from "./pages/AssetDetailPage";
import { QuotationsListPage } from "./pages/QuotationsListPage";
import { QuotationBuilderPage } from "./pages/QuotationBuilderPage";
import { InvoicesListPage } from "./pages/InvoicesListPage";
import { InvoiceBuilderPage } from "./pages/InvoiceBuilderPage";
import { CompaniesListPage } from "./pages/CompaniesListPage";
import { CompanyDetailPage } from "./pages/CompanyDetailPage";
import { WorkOrdersListPage } from "./pages/WorkOrdersListPage";
import { WorkOrderDetailPage, LinkedDoc, WOLineItem } from "./pages/WorkOrderDetailPage";
import { ContractsListPage } from "./pages/ContractsListPage";
import { TicketsListPage } from "./pages/TicketsListPage";
import { TicketDetailPage } from "./pages/TicketDetailPage";
import { SettingsPage } from "./pages/SettingsPage";
import { PartsListPage } from "./pages/PartsListPage";
import FoundationsPage from "./pages/FoundationsPage";
import { SchedulerPage } from "./pages/SchedulerPage";

type AppView =
  | { type: "settings" }
  | { type: "foundations" }
  | { type: "assets-list" }
  | { type: "asset-detail"; assetId: string; assetName: string }
  | { type: "quotations-list" }
  | { type: "quotation-builder"; quoteNumber: string; linkedWo?: { number: string; title: string }; initialLineItems?: WOLineItem[] }
  | { type: "invoices-list" }
  | { type: "invoice-builder"; invoiceNumber: string; poNumber?: string; poDocument?: File; initialStatus?: "draft" | "sent" | "paid" | "overdue"; linkedWo?: { number: string; title: string }; initialLineItems?: WOLineItem[] }
  | { type: "companies-list" }
  | { type: "company-detail"; companyId: string; companyName: string }
  | { type: "work-orders-list" }
  | { type: "work-order-detail"; workOrderId: string; linkedTicketId?: string }
  | { type: "contracts-list" }
  | { type: "tickets-list" }
  | { type: "ticket-detail"; ticketId: string }
  | { type: "parts-list" }
  | { type: "scheduler" }
  | { type: "placeholder"; page: ActivePage };

function Toast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 1000,
      background: "#111827", color: "#fff",
      padding: "12px 16px 12px 14px", borderRadius: 10,
      fontSize: 13, fontWeight: 500, fontFamily: "var(--font-family-base)",
      boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="9" fill="#059669" />
        <path d="M5.5 9l2.5 2.5 5-5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {message}
      <button
        onClick={onDismiss}
        style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", padding: 0, marginLeft: 4, display: "flex", alignItems: "center" }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M2 2l8 8M10 2L2 10" />
        </svg>
      </button>
    </div>
  );
}

type EmailSentData =
  | { docType: "Quotation"; docNumber: string; fromEmail: string; toEmail: string; toName: string; subject: string; body: string }
  | { docType: "Invoice"; docNumber: string; fromEmail: string; toEmail: string; toName: string; subject: string; body: string };

function AppContent({ onNavigateRoot }: { onNavigateRoot: (view: RootView) => void }) {
  const { addThread } = useWOEmails();
  const [activePage, setActivePage] = React.useState<ActivePage>("Assets");
  const [view, setView] = React.useState<AppView>({ type: "assets-list" });
  const [toast, setToast] = React.useState<string | null>(null);
  const [linkedDocsMap, setLinkedDocsMap] = React.useState<Record<string, LinkedDoc[]>>({});
  const [ticketLinkedWoMap, setTicketLinkedWoMap] = React.useState<Record<string, string>>({});

  function handleEmailSent(woNumber: string | undefined, data: EmailSentData) {
    if (!woNumber) return;
    const now = new Date();
    const dateStr =
      now.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) +
      " - " +
      now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const newThread: EmailThread = {
      id: `thread-${Date.now()}`,
      subject: data.subject,
      preview: data.body.slice(0, 120),
      senderName: "James McDonald",
      senderInitials: "JM",
      lastDate: dateStr,
      replyCount: 1,
      peopleCount: 2,
      unreadCount: 0,
      messages: [
        {
          id: `msg-${Date.now()}`,
          sender: "James McDonald",
          senderInitials: "JM",
          isOutgoing: true,
          timestamp: now.toLocaleString("en-GB"),
          toLine: `To: ${data.toName}`,
          body: data.body,
        },
      ],
      linkedDoc: { type: data.docType === "Invoice" ? "invoice" : "quotation", number: data.docNumber },
    };
    addThread(woNumber, newThread);
  }

  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function handleNavigate(page: ActivePage) {
    setActivePage(page);
    if (page === "Assets") {
      setView({ type: "assets-list" });
    } else if (page === "Quotation") {
      setView({ type: "quotations-list" });
    } else if (page === "Design System" || page === "Help Center") {
      setView({ type: "foundations" });
    } else if (page === "Invoices") {
      setView({ type: "invoices-list" });
    } else if (page === "Companies") {
      setView({ type: "companies-list" });
    } else if (page === "Work Orders" || page === "My Work Orders") {
      setView({ type: "work-orders-list" });
    } else if (page === "Contracts") {
      setView({ type: "contracts-list" });
    } else if (page === "Parts") {
      setView({ type: "parts-list" });
    } else if (page === "Tickets") {
      setView({ type: "tickets-list" });
    } else if (page === "Settings") {
      setView({ type: "settings" });
    } else if (page === "Scheduler") {
      setView({ type: "scheduler" });
    } else {
      setView({ type: "placeholder", page });
    }
  }

  function handleOpenQuotation(id: string) {
    const num = String(id).padStart(4, "0");
    setView({ type: "quotation-builder", quoteNumber: `QT-2026-${num}` });
  }

  function handleCreateNew() {
    const nextNum = `QT-2026-${String(Math.floor(Math.random() * 9000) + 1000)}`;
    setView({ type: "quotation-builder", quoteNumber: nextNum });
  }

  function handleOpenInvoice(invoiceNumber: string) {
    setView({ type: "invoice-builder", invoiceNumber });
  }

  function handleCreateInvoice() {
    const nextNum = `IN-2026-${String(Math.floor(Math.random() * 9000) + 1000)}`;
    setView({ type: "invoice-builder", invoiceNumber: nextNum });
  }

  function handleOpenWorkOrder(id: string) {
    setView({ type: "work-order-detail", workOrderId: id });
  }

  function handleCreateWorkOrder() {
    setView({ type: "work-order-detail", workOrderId: "new" });
  }

  function handleCreateQuoteFromWO(woNumber: string, woTitle: string, lineItems: WOLineItem[]) {
    const nextNum = `QT-2026-${String(Math.floor(Math.random() * 9000) + 1000)}`;
    const today = new Date();
    const date = today.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    const newDoc: LinkedDoc = { type: "quote", number: nextNum, status: "DRAFT", date };
    setLinkedDocsMap(prev => ({ ...prev, [woNumber]: [...(prev[woNumber] ?? []), newDoc] }));
    setActivePage("Quotation");
    setView({ type: "quotation-builder", quoteNumber: nextNum, linkedWo: { number: woNumber, title: woTitle }, initialLineItems: lineItems });
    setToast(`New quotation linked to ${woNumber}`);
  }

  function handleCreateInvoiceFromWO(woNumber: string, woTitle: string, lineItems: WOLineItem[]) {
    const nextNum = `IN-2026-${String(Math.floor(Math.random() * 9000) + 1000)}`;
    const today = new Date();
    const date = today.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    const newDoc: LinkedDoc = { type: "invoice", number: nextNum, status: "DRAFT", date };
    setLinkedDocsMap(prev => ({ ...prev, [woNumber]: [...(prev[woNumber] ?? []), newDoc] }));
    setActivePage("Invoices");
    setView({ type: "invoice-builder", invoiceNumber: nextNum, linkedWo: { number: woNumber, title: woTitle }, initialLineItems: lineItems });
    setToast(`New invoice linked to ${woNumber}`);
  }

  function renderContent() {
    switch (view.type) {
      case "settings":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <SettingsPage />
          </div>
        );
      case "assets-list":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <AssetsListPage onOpenAsset={(id, name) => setView({ type: "asset-detail", assetId: id, assetName: name })} />
          </div>
        );
      case "asset-detail":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <AssetDetailPage
              assetId={view.assetId}
              assetName={view.assetName}
              onBack={() => setView({ type: "assets-list" })}
              onCreateWorkOrder={() => { setActivePage("Work Orders"); setView({ type: "work-order-detail", workOrderId: "new" }); }}
            />
          </div>
        );
      case "foundations":
        return (
          <div style={{ flex: 1, overflowY: "auto" }}>
            <FoundationsPage />
          </div>
        );
      case "quotations-list":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <QuotationsListPage
              onOpenQuotation={handleOpenQuotation}
              onCreateNew={handleCreateNew}
            />
          </div>
        );
      case "quotation-builder":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <QuotationBuilderPage
              quoteNumber={view.quoteNumber}
              linkedWo={view.linkedWo}
              initialLineItems={view.initialLineItems}
              onSave={() => setView({ type: "quotations-list" })}
              onConvertToInvoice={(poNumber, poDocument) => {
                const invoiceNum = view.quoteNumber.replace("QT-", "IN-");
                setView({ type: "invoice-builder", invoiceNumber: invoiceNum, poNumber: poNumber || undefined, poDocument, initialStatus: "draft", linkedWo: view.linkedWo });
                setToast(`Quotation converted to Invoice ${invoiceNum}`);
              }}
              onNavigateToWorkOrder={(woNumber) => {
                setActivePage("Work Orders");
                setView({ type: "work-order-detail", workOrderId: woNumber });
              }}
              onEmailSent={(data) => handleEmailSent(view.linkedWo?.number, data)}
            />
          </div>
        );
      case "invoices-list":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <InvoicesListPage
              onOpenInvoice={handleOpenInvoice}
              onCreateInvoice={handleCreateInvoice}
            />
          </div>
        );
      case "invoice-builder":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <InvoiceBuilderPage
              invoiceNumber={view.invoiceNumber}
              poNumber={view.poNumber}
              poDocument={view.poDocument}
              initialStatus={view.initialStatus}
              linkedWo={view.linkedWo}
              initialLineItems={view.initialLineItems}
              onBack={() => setView({ type: "invoices-list" })}
              onNavigateToWorkOrder={(woNumber) => {
                setActivePage("Work Orders");
                setView({ type: "work-order-detail", workOrderId: woNumber });
              }}
              onEmailSent={(data) => handleEmailSent(view.linkedWo?.number, data)}
            />
          </div>
        );
      case "companies-list":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <CompaniesListPage
              onOpenCompany={(id, name) => setView({ type: "company-detail", companyId: id, companyName: name })}
            />
          </div>
        );
      case "company-detail":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <CompanyDetailPage
              companyId={view.companyId}
              companyName={view.companyName}
              onBack={() => setView({ type: "companies-list" })}
              onOpenCompany={(id, name) => setView({ type: "company-detail", companyId: id, companyName: name })}
            />
          </div>
        );
      case "work-orders-list":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <WorkOrdersListPage
              onOpenWorkOrder={handleOpenWorkOrder}
              onCreateNew={handleCreateWorkOrder}
            />
          </div>
        );
      case "work-order-detail":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <WorkOrderDetailPage
              workOrderId={view.workOrderId}
              onBack={() => { setActivePage("Work Orders"); setView({ type: "work-orders-list" }); }}
              onCreateQuote={handleCreateQuoteFromWO}
              onCreateInvoice={handleCreateInvoiceFromWO}
              linkedDocs={linkedDocsMap[view.workOrderId] ?? []}
              onOpenLinkedDoc={(doc) => {
                if (doc.type === "quote") {
                  setActivePage("Quotation");
                  setView({ type: "quotation-builder", quoteNumber: doc.number, linkedWo: { number: view.workOrderId, title: "Asset Repair & Maintenance" } });
                } else {
                  setActivePage("Invoices");
                  setView({ type: "invoice-builder", invoiceNumber: doc.number, linkedWo: { number: view.workOrderId, title: "Asset Repair & Maintenance" } });
                }
              }}
              linkedTicket={view.linkedTicketId ? {
                id: view.linkedTicketId,
                number: "TKT-10321",
                title: "Asset Repair & Maintenance",
                status: "OPEN",
              } : undefined}
              onOpenLinkedTicket={(ticketId) => {
                setActivePage("Tickets");
                setView({ type: "ticket-detail", ticketId });
              }}
            />
          </div>
        );
      case "parts-list":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <PartsListPage />
          </div>
        );
      case "scheduler":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <SchedulerPage />
          </div>
        );
      case "contracts-list":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <ContractsListPage />
          </div>
        );
      case "tickets-list":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <TicketsListPage onOpenTicket={(id) => setView({ type: "ticket-detail", ticketId: id })} />
          </div>
        );
      case "ticket-detail":
        return (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <TicketDetailPage
              ticketId={view.ticketId}
              linkedWoId={ticketLinkedWoMap[view.ticketId]}
              onAddWorkOrder={() => {
                setTicketLinkedWoMap(prev => ({ ...prev, [view.ticketId]: "WO-2026-0001" }));
                setToast("Work order WO-2026-0001 linked to ticket");
              }}
              onOpenWorkOrder={(woId) => {
                setActivePage("Work Orders");
                setView({ type: "work-order-detail", workOrderId: woId, linkedTicketId: view.ticketId });
              }}
              onBack={() => { setActivePage("Tickets"); setView({ type: "tickets-list" }); }}
            />
          </div>
        );
      case "placeholder":
        return (
          <div style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "var(--spacing-s)",
            background: "var(--color-bg-primary)",
          }}>
            <span style={{ fontSize: 40 }}>🚧</span>
            <p style={{ fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", margin: 0 }}>
              {view.page}
            </p>
            <p style={{ fontSize: 14, fontWeight: 500, color: "var(--color-content-secondary)", margin: 0 }}>
              This page is coming soon.
            </p>
          </div>
        );
    }
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <Sidebar activePage={activePage} onNavigate={handleNavigate} onNavigateRoot={onNavigateRoot} />
      {renderContent()}
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}

export default function FieldServiceApp({ onNavigateRoot }: { onNavigateRoot: (view: RootView) => void }) {
  return (
    <FinanceConfigProvider>
      <BusinessHoursProvider>
        <WOEmailsProvider>
          <AppContent onNavigateRoot={onNavigateRoot} />
        </WOEmailsProvider>
      </BusinessHoursProvider>
    </FinanceConfigProvider>
  );
}
